<template>
  <PageView class="with-submenu">
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="currentUser.login"
      role="navigation"
    />
    <h1
      :aria-label="$t('updateUser.titles', { userName: currentUser.login })"
      class="title main-title"
    >
      {{ $t("updateUser.titles", {userName: currentUser.login}) }}
    </h1>
    <div class="main-content UserView-card">
      <div class="card">
        <b-tabs
          v-model="selectedTab"
          :animated="true"
        >
          <b-tab-item
            :aria-label="currentUser.login"
            :label="currentUser.login"
            icon="user-astronaut"
            tabindex="0"
          >
            <div class="user-card">
              <div
                v-for="row in user.data"
                :key="row.colonne"
                class="user-field"
              >
                <!-- LABEL -->
                <div class="user-label">
                  <span v-if="row.colonne === 'id'">{{ $t("updateUser.columns.id") }}</span>
                  <span v-else-if="row.colonne === 'login'">{{ $t("updateUser.columns.login") }}</span>
                  <span v-else-if="row.colonne === 'email'">{{ $t("updateUser.columns.email") }}</span>
                  <span v-else-if="row.colonne === 'authorizedForApplicationCreation'">
                    {{ $t("updateUser.columns.authorizedForApplicationCreation") }}
                  </span>
                  <span v-else-if="row.colonne === 'openAdomAdmin'">
                    {{ $t("updateUser.columns.openAdomAdmin") }}
                  </span>
                  <span v-else-if="row.colonne === 'authorizations'">
                    {{ $t("updateUser.columns.authorizations") }}
                  </span>
                  <span v-else-if="row.colonne === 'chartes'">
                    {{ $t("updateUser.columns.chartes") }}
                  </span>
                </div>

                <!-- VALEUR -->
                <div class="user-value">
                  <!-- string -->
                  <span v-if="typeof row.valeur === 'string'">
                    {{ row.valeur }}
                  </span>

                  <!-- boolean -->
                  <span v-else-if="typeof row.valeur === 'boolean'">
                    <b-icon
                      :type="row.valeur ? 'is-success' : 'is-danger'"
                      icon="circle"
                    />
                  </span>

                  <!-- liste (chartes) -->
                  <b-taglist v-else-if="row.colonne === 'chartes'">
                    <b-tag
                      v-for="appName in [...new Set(charteApplications)]"
                      :key="appName"
                      type="is-info is-light"
                    >
                      <a
                        v-if="listApplication[appName]"
                        :href="'/applications/' + listApplication[appName]"
                      > {{ appName }}</a>
                      <p v-else>
                        {{ appName }}
                      </p>
                    </b-tag>
                  </b-taglist>
                </div>
              </div>
            </div>
          </b-tab-item>
          <b-tab-item
            :aria-label="$t('updateUser.update-email')"
            :label="$t('updateUser.update-email')"
            icon="envelope"
            tabindex="0"
          >
            <EditEmail
              :email="currentUser.email"
              :login="currentUser.login"
              @changed-email="emailChanged"
            />
          </b-tab-item>
          <b-tab-item
            :aria-label="$t('updateUser.update-pwd')"
            :label="$t('updateUser.update-pwd')"
            icon="unlock"
            tabindex="0"
          >
            <EditPwd
              :login="currentUser.login"
              @changed-password="passwordChanged"
            />
          </b-tab-item>
        </b-tabs>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
import PageView from "@/views/common/PageView.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import EditEmail from "@/components/common/users/EditEmail.vue";
import EditPwd from "@/components/common/users/EditPwd.vue";
import services from "@/composable/services";
import useArray from "@/composable/components/array";
import useObject from "@/composable/components/object";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import useNumber from "@/composable/components/number";
import "@/style/views/user/UserView.scss";
import { BTabItem, BTable, BTableColumn, BTabs, BTag, BTaglist } from "buefy";
import { CreateUserResult, LoginAdminResult } from "@/types/generated/UsedGeneratedTypes";
import { resolveAccessibleApplicationIds } from "@/composable/users/accessibleApplicationsResolver";

export default {
  components: { BTag, BTaglist, BTabs, BTableColumn, BTable, BTabItem, EditPwd, EditEmail, PageView, SubMenu },
  props: {
    userIdOrLogin: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const selectedTab = useNumber().refNumber;
    const { shallowRefArray: subMenuPaths, doChangeArray: changesubMenuPath } = useArray();
    const currentUser = services.loginService.getStoredAuthenticatedUser() || {} as LoginAdminResult;
    const { reactiveObject: user, doChangeObject: createUser } = useObject();

    function changeTabToSignIn() {
      selectedTab.value = 0;
    }

    function emailChanged(event: CreateUserResult) {
      changeTabToSignIn();
      currentUser.email = event.email;
      currentUser.state = event.accountState;
      services.loginService.updateAuthenticateduser(event);
      init();
      services.alertService.toastSuccess(t("alert.user-email-updated"));
    }

    function passwordChanged() {
      changeTabToSignIn();
      services.alertService.toastSuccess(t("alert.user-pwd-updated"));
    }

    onMounted(async () => {
      changesubMenuPath([
        new SubMenuPath(
          t("login.profil").toLowerCase(),
          () => {
          },
          () => router.push("/applications"),
        ),
      ]);
      await init();
    });
    const charteApplications = ref<string[]>([]);
    const listApplication = ref<{}>({});

    async function loadCharteApplications(chartes: Record<string, unknown>) {
      const ids = Object.keys(chartes);

      charteApplications.value = await Promise.all(
        ids.map(async (id) => {
          return await getNameApplication(id);
        }),
      );
    }

    async function getNameApplication(id: string): Promise<string> {
      const application = await services.applicationService.getApplication(
        id,
        ["CONFIGURATION"],
      );
      if (application && application.localName) {
        listApplication.value[application.localName] = application.name;
      }
      return application ? (application.localName || application.name) : id;
    }

    /**
     * Récupère via le endpoint streaming {@code GET /applications} la
     * liste de tous les systèmes d'information existants . Utilisé
     * uniquement quand l'utilisateur est {@code openAdomAdmin} pour
     * afficher la totalité des accès sans dépendre du record chartes
     * ( un admin a accès global , il n'a pas besoin d'avoir signé
     * chaque charte ) .
     *
     * <p>{@code getApplications} est en streaming NDJSON et ne renvoie
     * pas de Promise utilement awaitable ( fire-and-forget côté
     * service ) . On encapsule donc l'appel dans une Promise locale
     * qui résout lorsque le backend émet le marqueur de fin
     * {@code REACTIVE_PROGRESS result=1} . Un garde-fou timeout
     * 10s assure la résolution même si le flux est interrompu côté
     * réseau , pour éviter de bloquer l'affichage de la fiche .
     */
    function fetchAllApplicationIds(): Promise<string[]> {
      return new Promise<string[]>((resolve) => {
        const ids: string[] = [];
        let resolved = false;
        const safetyTimeoutMs = 10_000;
        const safetyTimer = setTimeout(() => {
          if (resolved) return;
          resolved = true;
          resolve(ids);
        }, safetyTimeoutMs);
        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          clearTimeout(safetyTimer);
          resolve(ids);
        };
        services.applicationService.getApplications(
          ((event: { type?: string; result?: unknown }) => {
            if (event?.type === "REACTIVE_RESULT") {
              const result = event.result as { application?: { id?: string } } | undefined;
              const id = result?.application?.id;
              if (typeof id === "string" && id) ids.push(id);
              return;
            }
            if (event?.type === "REACTIVE_PROGRESS"
              && typeof event.result === "number"
              && event.result >= 1) {
              finish();
            }
          }) as unknown as (message: string) => void,
          ["CONFIGURATION"],
        );
      });
    }

    async function init() {
      try {
        const data = Object.entries(currentUser)
          .filter(entry => entry[0] !== "currentUserRoles" && entry[0] !== "state"
            && (entry[0] !== "authorizations" && entry[1].length !== 0))
          .filter(async ([key, value]) => {
            if (key === "authorizations") {
              return currentUser.openAdomAdmin
                && currentUser.authorizedForApplicationCreation
                && Array.isArray(value)
                && value.length > 0;
            }

            if (key === "chartes") {
              // L'admin OpenADOM a accès à toutes les applications par
              // bypass , on force l'affichage de la ligne même si son
              // record chartes est vide ( cf ticket UX : champ vide
              // était trompeur pour le profil admin ) .
              if (currentUser.openAdomAdmin) return true;
              return (
                value
                && typeof value === "object"
                && !Array.isArray(value)
                && Object.keys(value as Record<string, unknown>).length > 0
              );
            }

            return value !== "" && value !== null && value !== undefined;
          })
          .map(([key, value]) => ({
            colonne: key,
            valeur: value,
          }));

        // Une ligne "chartes" peut manquer dans data quand l'admin
        // n'avait aucune charte signée ( record absent du DTO backend ) .
        // On l'injecte pour que l'UI rende toujours la section accès
        // global , peuplée plus bas par fetchAllApplicationIds .
        if (currentUser.openAdomAdmin && !data.some(row => row.colonne === "chartes")) {
          data.push({ colonne: "chartes", valeur: {} as unknown });
        }

        // Résolution unifiée des ids accessibles via helper pur :
        //   admin -> toutes les apps ; non-admin -> ids du record chartes .
        const chartes = currentUser.chartes as Record<string, unknown> | undefined;
        const charteIds = chartes && typeof chartes === "object" && !Array.isArray(chartes)
          ? Object.keys(chartes)
          : [];
        const allApplicationIds = currentUser.openAdomAdmin
          ? await fetchAllApplicationIds()
          : [];
        const accessibleIds = resolveAccessibleApplicationIds({
          isOpenAdomAdmin: !!currentUser.openAdomAdmin,
          charteIds,
          allApplicationIds,
        });
        if (accessibleIds.length > 0) {
          await loadCharteApplications(
            Object.fromEntries(accessibleIds.map(id => [id, true])),
          );
        }
        else {
          charteApplications.value = [];
        }

        createUser({
          data,
          columns: [
            { field: "colonne", label: "Colonne" },
            { field: "valeur", label: "Valeur" },
          ],
        });
      }
      catch (error) {
        changeTabToSignIn();
        services.alertService.toastError("" + error);
      }
    }

    return {
      selectedTab,
      subMenuPaths,
      user,
      currentUser,
      listApplication,
      changeTabToSignIn,
      emailChanged,
      passwordChanged,
      charteApplications,
    };
  },
};
</script>
